import { StatusCode } from 'grpc-web'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { Bundle } from './bundle'
import { Id } from 'types/definitions'

/**
 * How many skipped bundles to name in a partial-success alert before
 * summarizing the rest as "+N more".
 */
const maxNamedBundles = 5

export interface BulkActionResultMessages {
  success: (succeededCount: number) => string
  partialTitle: string
  partial: (succeededCount: number, skipped: Bundle[]) => string
}

/**
 * Shows a single success or partial-success alert for a bulk action whose
 * response reports skipped bundles by id. Both the price-edit and archive
 * responses share this errors-only shape: any sent bundle absent from the
 * skip set succeeded.
 */
export function showBulkActionResult(
  newAlert: AlertFactory,
  sent: Bundle[],
  skippedIds: Set<Id>,
  messages: BulkActionResultMessages,
) {
  const skipped = sent.filter((bundle) => skippedIds.has(bundle.Id))
  const succeededCount = sent.length - skipped.length

  if (skipped.length === 0) {
    newAlert()
      .withState(AlertState.Success)
      .saying('Success', messages.success(succeededCount))
      .autoDismiss()
      .show()
    return
  }

  newAlert()
    .withState(AlertState.Error)
    .saying(messages.partialTitle, messages.partial(succeededCount, skipped))
    .autoDismiss()
    .show()
}

/**
 * Shows the error alert for a failed bulk action. `PermissionDenied` and
 * `InvalidArgument` get specific messaging; anything else is logged and shown
 * as a generic failure.
 */
export function showBulkActionError(
  newAlert: AlertFactory,
  error: any,
  permissionMessage: string,
  genericMessage: string,
) {
  if (error?.code === StatusCode.PERMISSION_DENIED) {
    newAlert()
      .withState(AlertState.Error)
      .saying('Error', permissionMessage)
      .autoDismiss()
      .show()
    return
  }

  if (error?.code === StatusCode.INVALID_ARGUMENT) {
    newAlert()
      .withState(AlertState.Error)
      .saying('Error', error.message)
      .autoDismiss()
      .show()
    return
  }

  console.error(error)

  newAlert()
    .withState(AlertState.Error)
    .saying('Error', genericMessage)
    .autoDismiss()
    .show()
}

export function pluralizeBundles(count: number) {
  return count === 1 ? 'bundle' : 'bundles'
}

export function formatBundleNames(bundles: Bundle[]) {
  const names = bundles.slice(0, maxNamedBundles).map((bundle) => bundle.name)
  const remaining = bundles.length - names.length

  return remaining > 0
    ? `${names.join(', ')} +${remaining} more`
    : names.join(', ')
}
